/** parse string literal into tokens and return it if it is comprised solely of
 * PermittedTokens otherwise return never */
export type TokenListGuard<
    TokenList,
    PermittedTokens extends string,
    PermittedUnion = Split<PermittedTokens>[number]
> = TokenList extends PermittedUnion | ''
    ? TokenList
    : TokenList extends `${infer First} ${infer Rest}`
    ? First extends PermittedUnion
        ? `${First} ${TokenListGuard<Rest, PermittedTokens>}`
        : Rest extends PermittedUnion
        ? Rest
        : never
    : never;

// general typescript utilities

export type Split<
    StringList extends string,
    Separator extends string = ' '
> = StringList extends `${infer T}${Separator}${infer Rest}`
    ? T extends ''
        ? [...Split<Rest>]
        : T extends string
        ? [T, ...Split<Rest>]
        : [...Split<Rest>]
    : [StringList];

export function split<T extends string | undefined = ''>(
    arg: T
): T extends string ? Split<T> : Split<''> {
    return (typeof arg === 'undefined' ? [] : arg.split(' ')) as T extends string
        ? Split<T>
        : Split<''>;
}

export function omit<T extends Readonly<{ [key: string]: unknown }>, K extends string>(
    arg: T,
    keys: K[]
): Omit<T, K> {
    // actually write better filter!?
    const shallowClone = { ...arg };
    for (const key of keys) {
        delete shallowClone[key];
    }
    return shallowClone;
}

// use some secret typescript magic to transform unions to intersections then back to
// (different) unions
// https://stackoverflow.com/questions/50374908/transform-union-type-to-intersection-type
// and
// https://stackoverflow.com/questions/55904032/how-to-get-optional-property-from-union

type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (
    k: infer I
) => void
    ? I
    : never;

type ValueOfUnion<T, K> = T extends unknown ? (K extends keyof T ? T[K] : undefined) : never;

type UnionMapping<T> = {
    [K in keyof UnionToIntersection<T> | keyof T]: ValueOfUnion<T, K>;
};

export type UnionMerge<T> = Pick<UnionMapping<T>, keyof T> & Partial<UnionMapping<T>>;
